import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, map, Observable, Subscription, take } from 'rxjs';

import { DurationColumnFormat } from '@blaze-shared/api-contracts-domain';
import {
  type ElementFormState,
  ElementVerticalAlignment,
  InputElementMetaUiFeatures,
  makeFontStyles,
  makeFontStyleStyles,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { getBackground } from '@blaze-shared/elements-domain';
import { getDisplayStringFromLinkValue } from '@blaze-shared/schema-domain';
import {
  type ElementSchema,
  isDatasetLinkValue,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import { safeDetectChanges } from '@blaze-shared/shared-ui';
import { useEmitTracker } from '@blaze-shared/shared-util';
import { type Nullish } from '@blaze-shared/shared-util';

import { CanvasElementComponent } from '../../canvas-elements/element-component';
import { ElementLabelComponent } from '../../components/element-label/element-label.component';
import { CanvasElementBaseComponent } from '../__base/base.component';

import { BarcodeScannerOverlayComponent } from './barcode-scanner-overlay/barcode-scanner-overlay.component';
import { getInputValidationConfig, InputConfig } from './barcode-scanner-validation';

@UntilDestroy()
@Component({
  selector: 'be-barcode-scanner',
  imports: [
    AsyncPipe,
    CommonModule,
    ElementLabelComponent,
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
  ],
  templateUrl: './barcode-scanner.component.html',
  styleUrls: ['./barcode-scanner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarcodeScannerComponent
  extends CanvasElementBaseComponent<'barcodeScanner'>
  implements CanvasElementComponent<'barcodeScanner'>, OnChanges, OnInit
{
  private overlay = inject(Overlay);

  @Input() value?: Nullish<string>;
  @Input() formState?: ElementFormState;

  @ViewChild('inputText') inputText?: ElementRef<HTMLInputElement>;

  @HostBinding('attr.data-prevented') submitPrevented: boolean | undefined = undefined;

  private dataValueFeaturesFetched = false;
  private isFocusedValue = false;
  private lastScannedValue = '';
  private outputEmitTracker = useEmitTracker();
  private overlayRef?: OverlayRef;
  private scanSub?: Subscription;
  private wasEmitted = false;

  readonly elementKeyRoot = 'b-bs';

  wrapperSectionKey!: string;

  labelEnabled!: boolean;
  labelText!: string;
  placeholder = '';

  format?: TableColumnFormat;
  prefix!: string;
  suffix!: string;
  maximum?: number;
  minimum?: number;
  isScannerOn = false;
  isReadOnly = false;

  inputConfig: InputConfig = getInputValidationConfig('text');
  formControl = new UntypedFormControl('');
  elementFormat!: TableColumnFormat;

  durationFormat!: DurationColumnFormat;

  inputStep?: number | 'any';
  controlStyles!: Record<string, string>;
  formId?: string;
  inputStyles!: Record<string, string>;
  inputWrapperStyles!: Record<string, string>;
  submitOnEnter = false;
  textareaContainerClasses$?: Observable<string[]>;
  validationStyles!: Record<string, string>;
  labelVerticalAlign: ElementVerticalAlignment | undefined;

  set isFocused(value: boolean) {
    this.isFocusedValue = value;
    if (this.props?.style.input?.border.selectorType !== 'none') {
      const color = value
        ? this.props?.style.input?.accent.color1
        : this.props?.style.input?.border.color1;
      this.inputWrapperStyles['border'] = `1px solid ${color}`;
      this.cdr.detectChanges();
    }
    if (value === false) {
      this.updateInputControlAndPreformat(this.formControl.value);
    }
  }
  get isFocused(): boolean {
    return this.isFocusedValue;
  }

  get isParagraph(): boolean {
    return this.elementFormat === 'paragraph';
  }

  get showErrorLabel(): boolean {
    return !!(
      this.hasValidationError ||
      (this.formControl.invalid && this.formControl.dirty && this.inputConfig.invalidMsg)
    );
  }

  get hasValidationError(): boolean {
    return !!this.formState?.errors?.required || !!this.formState?.errors?.invalid;
  }

  get errorLabel(): string {
    return (
      this.inputConfig.invalidMsg ||
      this.formState?.messages?.required ||
      this.formState?.messages?.invalid ||
      ''
    );
  }

  constructor() {
    super();
    this.initMarginHandler((props) => props?.style?.general?.offset?.margin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['props']) {
      if (!this.wrapperSectionKey) {
        this.wrapperSectionKey = `section-${this.elementId}`;
      }
      const { title, input } = this.props?.content || {};

      this.labelEnabled = title?.enabled ?? true;
      this.placeholder = input?.placeholder || '';

      this.updateFormControl();
      this.updateStyles();
    }

    if (changes?.['props'] || changes?.['value']) {
      if (isDatasetLinkValue(this.value)) {
        this.value = getDisplayStringFromLinkValue(this.value.display);
      }
      // Precision change might affect the value
      if (this.shouldUpdateInputControl(this.value)) {
        this.updateInputControlAndPreformat(this.value);
      } else {
        // Just reemit the value further
        this.updateOutput(this.value ?? '');
      }
    }
  }

  ngOnInit(): void {
    this.textareaContainerClasses$ = combineLatest({
      props: this.props$,
      element: this.element$,
    }).pipe(
      map(({ props, element }) => {
        const isFixed = !!element.height?.fixed;
        const overflow = props?.content?.container?.overflow;
        const format = this.format ?? props?.content?.input?.format;
        if (format !== 'paragraph') {
          return [];
        }
        if (!isFixed) {
          return ['mode-full'];
        }
        return overflow === 'hidden' ? ['mode-fixed-clip'] : ['mode-fixed-scroll'];
      }),
    );

    /**
     * The updates to the form control invoke the output update logic
     */
    this.formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      this.updateOutput(value);
    });

    this.getElementUiFeatures<InputElementMetaUiFeatures>()
      .pipe(untilDestroyed(this))
      .subscribe((features) => {
        this.labelText = (features.title ?? '') + this.getRequiredSuffix(features);
        this.formId = features.formElementId;
        this.submitOnEnter = features.submitOnEnter;

        this.format = features.format;
        this.placeholder = this.props?.content?.input?.placeholder || '';
        this.isReadOnly = features.isReadOnly;

        this.dataValueFeaturesFetched = true;
        this.updateFormControl();
        this.updateInputControlAndPreformat(this.value);
        this.setDefaultEventSchema('change', this.getDefaultEventSchema('change'));
        this.setDefaultEventSchema('submit', this.getDefaultEventSchema('submit'));
        safeDetectChanges(this.cdr);
      });
  }

  override getDefaultEventSchema(eventType: RuntimeEventType): ElementSchema {
    const output = this.ioConfig?.outputs?.find((output) => output.eventType === eventType);
    const defaultSchema = output?.schema;

    const updatedColumns = defaultSchema?.columns ? [...defaultSchema.columns] : [];
    const column = updatedColumns[0];
    if (column) {
      updatedColumns[0] = {
        ...column,
        columnType: 'String',
        dataType: 'String',
      };
    }
    return this.createElementSchema(eventType, updatedColumns, undefined, defaultSchema);
  }

  onAreaClicked(event: MouseEvent): void {
    if (this.isReadOnly) {
      return;
    }
    if (
      (event.target as HTMLElement).id !== this.wrapperSectionKey ||
      !this.inputText?.nativeElement
    ) {
      return;
    }

    const editorBody = this.inputText.nativeElement;
    if (!editorBody) {
      return;
    }

    const yDiff = editorBody.offsetTop - event.offsetY;
    if (yDiff > 0) {
      // clicked above
      if (yDiff < 32 && event.offsetX > editorBody.offsetWidth / 2) {
        // but in the right half and close enough to the editor
        this.makeEditorSelection(true);
      } else {
        this.makeEditorSelection();
      }
    } else if (event.offsetY > editorBody.offsetTop + editorBody.offsetHeight) {
      // clicked below
      this.makeEditorSelection(true);
    }
  }

  onEnterPressed(): void {
    if (!this.submitOnEnter || !this.formId) {
      this.submitPrevented = true;
      return;
    }
    this.output.emit({
      eventType: 'submit',
      data: this.formId,
      isInitialData: !this.wasEmitted,
      schema: this.getDefaultEventSchema('submit'),
    });
  }

  reset(): void {
    this.outputEmitTracker.reset();
    this.wasEmitted = false;
    this.formControl.setValue('');
  }

  openScanner(): void {
    this.overlayRef = this.overlay.create();
    const scannerOverlay = new ComponentPortal(BarcodeScannerOverlayComponent);
    const componentRef = this.overlayRef.attach(scannerOverlay);
    this.scanSub = componentRef.instance.scanned$$.subscribe((scannedText) => {
      this.updateInputControlAndPreformat(scannedText, true);
      safeDetectChanges(this.cdr);
    });

    componentRef.instance.close$$.pipe(take(1)).subscribe(() => {
      this.scanSub?.unsubscribe();
      this.overlayRef?.detach();
      this.overlayRef?.dispose();
      this.lastScannedValue = '';
      this.overlayRef = undefined;
      safeDetectChanges(this.cdr);
    });
  }

  /**
   * This method recognizes whether a completely new value is set
   * or if it's being inputted by the user and goes through transient/undetermined states.
   * Also the input should be updated if the precision setting got modified.
   */
  private shouldUpdateInputControl(value: Nullish<string>): boolean {
    return value !== this.formControl.value;
  }

  private updateStyles(): void {
    const { style, content } = this.props || {};
    const { title, container } = content || {};
    const { general, input, inputFont, labelBlock } = style || {};
    if (!style || !title || !general || !input || !inputFont || !container || !labelBlock) {
      return;
    }

    const controlStyles: Record<string, string> = {};
    const validationStyles: Record<string, string> = {};
    const inputWrapperStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.value.color1,
      ...makeFontStyles(inputFont, { lineHeight: !this.isParagraph }),
    };
    const passwordIconStyles: Record<string, string> = {
      background: getBackground(input.fill),
      color: input.value.color1,
    };
    const inputStyles: Record<string, string> = {
      ...makeFontStyles(inputFont, { lineHeight: !this.isParagraph }),
      ...makeFontStyleStyles(inputFont.style),
      ['font-weight']: this.fontsService.getFontWeight(inputFont.fontFamily, inputFont.fontWeight),
    };

    if (title.enabled !== false) {
      if (title.position === 'top') {
        controlStyles['flex-direction'] = `column`;
      } else {
        validationStyles['margin-left'] = title.width;
        validationStyles['padding-left'] = '8px';
        inputWrapperStyles['flex-basis'] = `${100 - parseInt(title.width, 10)}%`;
      }
    }

    this.labelVerticalAlign =
      this.isParagraph && title.position === 'left' ? title.align.vertical : undefined;

    if (input.border.selectorType !== 'none') {
      inputWrapperStyles['border'] = `1px solid ${input.border.color1}`;
      passwordIconStyles['border'] = `1px solid ${input.border.color1}`;
    }
    if (input.radius !== '0px') {
      inputWrapperStyles['border-radius'] = input.radius;
      passwordIconStyles['border-radius'] = input.radius;
    }

    this.controlStyles = controlStyles;
    this.validationStyles = validationStyles;
    this.inputWrapperStyles = inputWrapperStyles;
    this.inputStyles = inputStyles;
  }

  private updateFormControl(): void {
    const input = this.props?.content?.input;
    if (!input) {
      return;
    }

    const format = this.format ?? input?.format;
    const foundConfig = getInputValidationConfig(format);

    this.elementFormat = format;
    this.inputStep = 'any';

    // Apply config
    if (foundConfig && foundConfig !== this.inputConfig) {
      this.inputConfig = foundConfig;
    }

    this.prefix = '';
    this.suffix = '';
  }

  private updateOutput(data: Nullish<string>): void {
    this.value = data;
    if (this.outputEmitTracker.shouldEmit(data)) {
      this.output.emit({
        eventType: 'change',
        data,
        isInitialData: !this.wasEmitted,
        schema: this.getDefaultEventSchema('change'),
      });
      this.wasEmitted = true;
    }
  }

  /**
   * Used to populate the field when the data comes from the input
   * or ensures the proper display format after the user blurs the input field
   */
  private updateInputControlAndPreformat(data: Nullish<string>, isScannedText = false): void {
    if (
      !this.dataValueFeaturesFetched ||
      (isScannedText && data && data === this.lastScannedValue)
    ) {
      return;
    }
    let value = data ?? '';
    this.lastScannedValue = value;

    if (isScannedText && this.isParagraph) {
      value = `${this.formControl.value}${value}\n`;
    }
    this.formControl.setValue(value);

    if (isScannedText) {
      this.onEnterPressed();
    }
  }

  private makeEditorSelection(goToEnd = false): void {
    const input = this.inputText?.nativeElement;
    if (!input) {
      return;
    }

    const point = goToEnd ? input.value.length : 0;
    input.focus();
    const inputType = input.type;
    input.type = 'text';
    input.selectionStart = point;
    input.selectionEnd = point;
    input.type = inputType;
  }

  // Barcode Scanner
}
